import React, { useEffect, useRef } from "react";

/**
 * Waveform: beautiful EQ visualisation with dramatic spikes when active.
 * Uses an audioLevel value (0..1) sampled from a parent's AnalyserNode loop,
 * plus a smoothed phase for the idle "breath" animation.
 */
export default function Waveform({ level = 0, active = false, color = "#8fb8e0", barCount = 24, analyser = null }) {
  const ref = useRef(null);
  const stateRef = useRef({ 
    heights: new Array(barCount).fill(0.05), 
    peaks: new Array(barCount).fill(0.05),
    peakDecay: new Array(barCount).fill(0),
    t: 0,
    freqData: null
  });

  useEffect(() => {
    let raf;
    const draw = () => {
      const c = ref.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      const w = c.width;
      const h = c.height;
      ctx.clearRect(0, 0, w, h);

      const s = stateRef.current;
      s.t += 0.05; // Slower idle breath

      // Try to get real frequency data if analyser exists
      let useRealData = false;
      if (analyser && active) {
        if (!s.freqData || s.freqData.length !== analyser.frequencyBinCount) {
          s.freqData = new Uint8Array(analyser.frequencyBinCount);
        }
        analyser.getByteFrequencyData(s.freqData);
        useRealData = true;
      }

      const barW = w / (barCount * 1.6);
      const gap = (w - barW * barCount) / (barCount - 1);

      // Determine max frequency magnitude in this frame (for dynamic scaling)
      let maxFreq = 1;
      if (useRealData) {
        for (let i = 0; i < s.freqData.length; i++) {
          if (s.freqData[i] > maxFreq) maxFreq = s.freqData[i];
        }
      }

      for (let i = 0; i < barCount; i++) {
        let target = 0;
        
        if (useRealData) {
          // Map bar index to frequency bins (focusing on vocal ranges, lower/mid frequencies)
          // We'll map the 20 bars to the first ~50% of the frequency bins (since high frequencies are often noise)
          const usableBins = Math.floor(s.freqData.length * 0.5);
          const binsPerBar = Math.max(1, Math.floor(usableBins / barCount));
          const startIndex = i * binsPerBar;
          
          let binSum = 0;
          for (let j = 0; j < binsPerBar; j++) {
             binSum += s.freqData[startIndex + j] || 0;
          }
          let binAvg = binSum / binsPerBar;
          
          // Normalize (0-255) and apply a slight non-linear curve for punchiness
          let normalized = binAvg / 255.0;
          
          // Add a subtle bell curve weighting so edges don't disappear entirely
          const distance = Math.abs(i - (barCount - 1) / 2) / ((barCount - 1) / 2);
          const lobeWeight = 0.8 + 0.4 * (1 - distance); // Boost center slightly
          
          target = Math.max(0.02, normalized * lobeWeight * 1.5);
        } else {
          // Fallback / Idle animation
          const baseLevel = active ? Math.max(0.08, level * 1.5) : 0.015 + 0.008 * Math.sin(s.t * 1.5);
          const distance = Math.abs(i - (barCount - 1) / 2) / ((barCount - 1) / 2);
          const lobe = 1 - 0.4 * distance * distance;
          
          let noise = 0.5 + 0.5 * Math.sin(s.t + i * 0.5);
          if (active) {
            const spikeChance = Math.random();
            if (spikeChance > 0.6) noise = 0.7 + 0.3 * Math.random();
            else if (spikeChance > 0.3) noise = 0.3 + 0.4 * Math.random();
            else noise = 0.1 + 0.2 * Math.random();
          }
          target = Math.max(0.03, baseLevel * lobe * noise * 1.6);
        }

        // Fast attack, smooth decay
        const attackSpeed = useRealData ? 0.6 : (active ? 0.55 : 0.25);
        const decaySpeed = useRealData ? 0.25 : (active ? 0.2 : 0.15);
        const speed = target > s.heights[i] ? attackSpeed : decaySpeed;
        
        s.heights[i] += (target - s.heights[i]) * speed;

        // Peak indicator — holds a peak then decays slowly
        if (s.heights[i] > s.peaks[i]) {
          s.peaks[i] = s.heights[i];
          s.peakDecay[i] = 0;
        } else {
          s.peakDecay[i] += 0.008;
          s.peaks[i] -= s.peakDecay[i] * 0.01;
          s.peaks[i] = Math.max(s.peaks[i], 0.03);
        }

        const bh = Math.max(2, s.heights[i] * h);
        const peakY = Math.max(2, s.peaks[i] * h);
        const x = i * (barW + gap);
        const y = (h - bh) / 2;
        const r = Math.min(barW / 2, bh / 2);

        // Draw bar with gradient
        const gradient = ctx.createLinearGradient(x, y, x, y + bh);
        gradient.addColorStop(0, color);
        gradient.addColorStop(0.5, color);
        gradient.addColorStop(1, color + (active ? "cc" : "88"));
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + barW - r, y);
        ctx.quadraticCurveTo(x + barW, y, x + barW, y + r);
        ctx.lineTo(x + barW, y + bh - r);
        ctx.quadraticCurveTo(x + barW, y + bh, x + barW - r, y + bh);
        ctx.lineTo(x + r, y + bh);
        ctx.quadraticCurveTo(x, y + bh, x, y + bh - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.fill();

        // Draw peak dot when active
        if (active && s.peaks[i] > 0.1) {
          const peakDotY = (h - peakY) / 2;
          ctx.fillStyle = color;
          ctx.globalAlpha = 0.7;
          ctx.beginPath();
          ctx.arc(x + barW / 2, peakDotY, Math.max(1, barW / 3), 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }

        // Glow effect for tall bars when active
        if (active && s.heights[i] > 0.3) {
          ctx.fillStyle = color;
          ctx.globalAlpha = 0.15;
          const glowH = bh * 1.3;
          const glowY = (h - glowH) / 2;
          ctx.beginPath();
          ctx.moveTo(x + r, glowY);
          ctx.lineTo(x + barW - r, glowY);
          ctx.quadraticCurveTo(x + barW, glowY, x + barW, glowY + r);
          ctx.lineTo(x + barW, glowY + glowH - r);
          ctx.quadraticCurveTo(x + barW, glowY + glowH, x + barW - r, glowY + glowH);
          ctx.lineTo(x + r, glowY + glowH);
          ctx.quadraticCurveTo(x, glowY + glowH, x, glowY + glowH - r);
          ctx.lineTo(x, glowY + r);
          ctx.quadraticCurveTo(x, glowY, x + r, glowY);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [level, active, color, barCount, analyser]);

  return (
    <canvas
      ref={ref}
      width={280}
      height={56}
      className="w-full h-14"
      data-testid="voice-waveform"
    />
  );
}
